// Vocabulary: readers see this feature as "owner reviews"; code, routes, DealHunt paths and
// analytics events keep the historical "owner note" name on purpose — do not rename them.

import { useEffect, useMemo, useRef } from 'react'
import styles from './OwnerHeroAsk.module.css'
import { useOnScreenOnce, useOwnerTrack } from 'lib/owner-notes/analytics'
import {
  BAND_CTA,
  DRAFT_WAITING_CTA,
  OWNERS_ANCHOR,
  OWNERS_SECTION_ID,
  RAIL_AUTHOR,
  railAskMode,
} from 'lib/owner-notes/band-copy'
import { requestOpenOwnerBand, useOwnNoteSignal } from 'lib/owner-notes/bus'
import { turnstileConfigured } from 'lib/owner-notes/turnstile'
import { signInGateFor } from 'lib/owner-notes/signin-gate'
import { useBandDraftWaiting } from 'lib/owner-notes/draft-waiting'
import { useWriterHandoff } from 'lib/community/use-writer-handoff'
import HandoffHint from 'components/owner-notes/HandoffHint'
import Linked from 'shared-components/service/Linked'

interface OwnerHeroAskProps {
  nodeId: string
  productName: string
  title: string
  text: string
  /** Where the `ask_shown` impression and `band_opened` say it came from. */
  location: 'rail'
  /** This product page (`dealHuntProductHref`) — the writer handoff's product. */
  productHref?: string | null
}

/**
 * The hero's one ask — in the rail card (`OwnerRailAsk`) before the first note, under the
 * reader (`OwnerNotesReader`) from it. It opens the Owners section's band; the hero never hosts
 * the form. Impression once per page view, only for a real ask, only once on screen (the hero
 * is below the fold on a phone).
 */
const OwnerHeroAsk: React.VFC<OwnerHeroAskProps> = ({
  nodeId,
  productName,
  title,
  text,
  location,
  productHref = null,
}) => {
  const product = useMemo(() => ({ nodeId, name: productName }), [nodeId, productName])
  const trackOwner = useOwnerTrack(product)
  const enabled = turnstileConfigured()
  const mode = railAskMode(useOwnNoteSignal())
  const asking = enabled && mode === 'ask'
  // One Review Writer (P1): with the flag on for `rail`, the ask links the community writer
  // (back to this page's #owners) instead of opening the Owners section's band.
  const handoff = useWriterHandoff(location, productHref, { hash: OWNERS_ANCHOR })
  // Gated, in place: a started draft in this tab (after mount only) turns the ask into "Finish".
  const draftWaiting = useBandDraftWaiting(nodeId, !handoff && signInGateFor('product'), mode)
  const askRef = useRef<HTMLDivElement>(null)
  const seen = useOnScreenOnce(askRef, asking)
  const shown = useRef(false)
  useEffect(() => {
    if (asking && seen && !shown.current) {
      shown.current = true
      trackOwner('ask_shown', { location })
    }
  }, [trackOwner, asking, seen, location])

  if (mode === 'author') {
    return (
      <div className={styles.ask}>
        <div className={styles.askText}>
          <p className={styles.askTitle}>{RAIL_AUTHOR.title}</p>
        </div>
        <a href={`#${OWNERS_SECTION_ID}`} className={styles.askLink}>
          {RAIL_AUTHOR.cta}
        </a>
      </div>
    )
  }
  if (!enabled || mode === 'pending') {
    return null
  }
  return (
    <div className={styles.ask} ref={askRef}>
      <div className={styles.askText}>
        <p className={styles.askTitle}>{title}</p>
        <p className={styles.askFine}>{text}</p>
        {handoff && <HandoffHint text={handoff.hint} />}
      </div>
      {handoff ? (
        <Linked href={handoff.href()} className={styles.askBtn} {...handoff.linkProps}>
          {`${BAND_CTA} →`}
        </Linked>
      ) : (
        <a
          href={`#${OWNERS_SECTION_ID}`}
          className={styles.askBtn}
          onClick={e => {
            e.preventDefault()
            requestOpenOwnerBand(location)
          }}
        >
          {`${draftWaiting ? DRAFT_WAITING_CTA : BAND_CTA} →`}
        </a>
      )}
    </div>
  )
}

export default OwnerHeroAsk
